// Combined "Sessions" card — replaces the previously separate
// UpcomingSessionsCard + PastSessionsTodayCard. Renders today's chronology in
// one place: an "Earlier today" section above a "Coming up" section. Either
// section is optional; the card returns null only when both are empty.
//
// Why one card: from the patient's perspective these are one logical thing
// — "what's happening with my sessions today and beyond". Splitting them
// into two cards meant two slots in the customize order, two empty
// placeholders when neither had data, and two visually separate boxes for
// what reads as a single timeline.

import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import WidgetCard from './WidgetCard'
import { GhostButton, PrimaryButton } from '../../../layout/styledComponents'
import { parseUtcDate } from '../../../utils/dates'
import { usePatient } from '../../../context/PatientContext'
import { useReceiptDrawer } from '../../../hooks/useReceiptDrawer'
import ReceiptDrawer from '../../../components/ReceiptDrawer'

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin: 4px 0 8px;
  &:first-child {
    margin-top: 0;
  }
`

const List = styled.ul`
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${(p) =>
    p.$past
      ? p.theme?.color?.bgSubtle || '#F4F0E9'
      : p.theme?.color?.primarySoft || '#E8EEF6'};
  border: 1px solid
    ${(p) =>
      p.$past
        ? p.theme?.color?.border || '#E6E0D6'
        : p.theme?.color?.primarySoft || '#E8EEF6'};
  flex-wrap: wrap;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  &:hover {
    ${(p) =>
      p.$clickable &&
      `background: ${
        p.$past
          ? p.theme?.color?.primarySoft || '#E8EEF6'
          : p.theme?.color?.bgRaised || '#fff'
      };`}
  }
`

const When = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
  width: 110px;
`

const Detail = styled.div`
  flex: 1;
  min-width: 0;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Sub = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const StatusChip = styled.span`
  display: inline-flex;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$tone === 'urgent'
      ? p.theme?.color?.urgentSoft || '#FBEAE9'
      : p.$tone === 'success'
        ? p.theme?.color?.accentSoft || '#E1ECE7'
        : p.theme?.color?.bgRaised || '#fff'};
  color: ${(p) =>
    p.$tone === 'urgent'
      ? p.theme?.color?.urgent || '#C24D49'
      : p.$tone === 'success'
        ? p.theme?.color?.accent || '#3F7D58'
        : p.theme?.color?.inkMuted || '#6B7280'};
  margin-left: 6px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

function whenLabel(iso) {
  if (!iso) return ''
  const d = parseUtcDate(iso)
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow = d.toDateString() === tomorrow.toDateString()
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (isToday) return `Today · ${time}`
  if (isTomorrow) return `Tomorrow · ${time}`
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) +
    ' · ' + time
}

function isClientLocalToday(iso) {
  if (!iso) return false
  const d = parseUtcDate(iso)
  const now = new Date()
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  )
}

function statusTone(s) {
  const v = String(s || '').toUpperCase()
  if (v === 'CANCELLED' || v === 'NO_SHOW') return 'urgent'
  if (v === 'COMPLETED') return 'success'
  return 'default'
}

function statusLabel(s) {
  const v = String(s || '').toUpperCase()
  if (v === 'CANCELLED') return 'Cancelled'
  if (v === 'NO_SHOW') return 'No-show'
  if (v === 'COMPLETED') return 'Completed'
  return v.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
}

export default function SessionsTimelineCard({
  upcoming,
  past,
  // Suppress imminent-/joinable-/payment-blocked rows because LiveNowCard,
  // NextSessionCard and BlockedSessionsCard own those visuals. Mirrors the
  // skipIfJoinable behaviour the previous UpcomingSessionsCard had.
  suppressIfWithinMinutes = 90,
  skipIfJoinable = true,
}) {
  const navigate = useNavigate()
  const { currentProfile } = usePatient()
  const receiptDrawer = useReceiptDrawer()

  // Earlier today — server already clipped to IST today; we additionally
  // re-filter by client local toDateString() so non-IST users only see
  // their own day. Cap at 5 to keep the card compact.
  const earlierToday = (Array.isArray(past) ? past : [])
    .filter((s) => isClientLocalToday(s?.scheduledAt))
    .slice(0, 5)

  // Coming up — non-imminent, non-joinable, non-payment-blocked future
  // sessions (LiveNowCard / NextSessionCard / BlockedSessionsCard own the
  // others). Mirrors the old UpcomingSessionsCard filter.
  const comingUp = (Array.isArray(upcoming) ? upcoming : []).filter((s) => {
    if (!s || !s.scheduledAt) return false
    if (skipIfJoinable && (s.canJoinNow || s.paymentBlocked)) return false
    const min = (parseUtcDate(s.scheduledAt).getTime() - Date.now()) / 60000
    return min > suppressIfWithinMinutes
  })

  if (earlierToday.length === 0 && comingUp.length === 0) return null

  function handleDownload(s) {
    if (!currentProfile || !s?.eventId) return
    receiptDrawer.open(currentProfile, s)
  }

  const totalCount = earlierToday.length + comingUp.length
  const busyId = receiptDrawer.state.loading ? receiptDrawer.state.eventId : null
  const errorId = receiptDrawer.state.error ? receiptDrawer.state.eventId : null

  return (
    <WidgetCard
      title="Sessions"
      badge={totalCount > 1 ? `${totalCount} total` : undefined}
    >
      {earlierToday.length > 0 && (
        <>
          <SectionLabel>Earlier today</SectionLabel>
          <List>
            {earlierToday.map((s) => (
              <Row key={s.eventId} $past>
                <When>{whenLabel(s.scheduledAt)}</When>
                <Detail>
                  <Title>
                    {s.title || 'Session'}
                    <StatusChip $tone={statusTone(s.status)}>
                      {statusLabel(s.status)}
                    </StatusChip>
                  </Title>
                  <Sub>
                    {s.practitionerName}
                    {s.durationMinutes ? ` · ${s.durationMinutes} min` : ''}
                    {errorId === s.eventId
                      ? ' · couldn’t fetch receipt — try again'
                      : ''}
                  </Sub>
                </Detail>
                <Actions>
                  {s.receiptAvailable ? (
                    <PrimaryButton
                      onClick={() => handleDownload(s)}
                      disabled={busyId === s.eventId}
                      style={{ padding: '6px 12px', fontSize: 13 }}
                      title="Download receipt as PDF"
                    >
                      {busyId === s.eventId ? 'Preparing…' : '⬇  Receipt'}
                    </PrimaryButton>
                  ) : null}
                  <GhostButton
                    onClick={() => navigate(`/patient-home/sessions/${s.eventId}`)}
                    style={{ padding: '6px 12px', fontSize: 13 }}
                  >
                    Details
                  </GhostButton>
                </Actions>
              </Row>
            ))}
          </List>
        </>
      )}
      {comingUp.length > 0 && (
        <>
          <SectionLabel>Coming up</SectionLabel>
          <List>
            {comingUp.map((s) => (
              <Row
                key={s.eventId}
                $clickable
                onClick={() => navigate(`/patient-home/sessions/${s.eventId}`)}
              >
                <When>{whenLabel(s.scheduledAt)}</When>
                <Detail>
                  <Title>{s.title || 'Session'}</Title>
                  <Sub>
                    {s.practitionerName}
                    {s.durationMinutes ? ` · ${s.durationMinutes} min` : ''}
                  </Sub>
                </Detail>
              </Row>
            ))}
          </List>
        </>
      )}
      <ReceiptDrawer
        open={receiptDrawer.state.open}
        title={receiptDrawer.state.title || 'Receipt'}
        subtitle={receiptDrawer.state.subtitle}
        blobUrl={receiptDrawer.state.blobUrl}
        mimeType={receiptDrawer.state.mimeType}
        filename={receiptDrawer.state.filename}
        fallbackText={receiptDrawer.state.fallbackText}
        onClose={receiptDrawer.close}
      />
    </WidgetCard>
  )
}
